import { useCallback, useEffect } from 'react';

import { useTranslation } from '@lyearn/i18n';
import useFilterManagerWithState from '@/components/FilterManager/useFilterManagerWithState';
import useActionItems from '@/modules/tasks/features/actionItems/hooks/useActionItems';
import { RelatedEntityTypeEnum, TaskSelectionOperationEnum } from '@/types/schema';

import { useAddTaskModalContext } from '../components/AddTaskModalProvider';
import { adaptAppliedFilters, getSortAndFilterItems } from '../utils';
import useUpdateEntities from './useUpdateEntities';

type UseAddTasksProps = {
  entityId?: string;
  entityType?: RelatedEntityTypeEnum;
  onSubmit: (refetchData?: boolean) => void;
  onClose: () => void;
};

export default function useAddTasks(props: UseAddTasksProps) {
  const { t } = useTranslation();
  const { entityId, entityType, onSubmit, onClose } = props;
  const { isFetching, onAddEntities } = useUpdateEntities({
    entityId,
    entityType,
  });
  const { state, methods } = useAddTaskModalContext();
  const { onReset, onSelectAllChange, getSelectedCount } = methods;
  const sortAndFilterItems = getSortAndFilterItems(t);
  const filterManager = useFilterManagerWithState({});
  const actionItemsData = useActionItems({
    sort: filterManager.state.sort,
    filters: {
      ...filterManager.state.filter,
      title: filterManager.state.search,
    },
  });
  const { totalCount } = actionItemsData;
  const { isSelectAll, excludeIds: excludeTaskIds, includeIds: includeTaskIds } = state;

  const selectedRootTasksCount = getSelectedCount(totalCount);

  useEffect(() => {
    onReset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterManager.state.filter, filterManager.state.search]);

  useEffect(() => {
    if (
      !isSelectAll &&
      totalCount &&
      totalCount === selectedRootTasksCount &&
      excludeTaskIds.length === 0
    ) {
      onSelectAllChange();
    } else if (selectedRootTasksCount === 0 && includeTaskIds.length === 0) {
      onReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    excludeTaskIds.length,
    includeTaskIds.length,
    selectedRootTasksCount,
    totalCount,
    isSelectAll,
  ]);

  const onAdd = useCallback(async () => {
    const taskSelectionConfig = {
      operation: TaskSelectionOperationEnum.Add,
      includeTaskIds,
      excludeTaskIds,
      filter: isSelectAll
        ? adaptAppliedFilters(filterManager.state.filter, filterManager.state.search)
        : undefined,
    };
    await onAddEntities(taskSelectionConfig, selectedRootTasksCount, () => {
      onSubmit();
      onClose();
    });
  }, [
    excludeTaskIds,
    filterManager.state.filter,
    filterManager.state.search,
    includeTaskIds,
    isSelectAll,
    onAddEntities,
    onClose,
    onSubmit,
    selectedRootTasksCount,
  ]);

  return {
    data: actionItemsData,
    filterManager,
    sortAndFilterItems,
    onAdd,
    isAddingTasks: isFetching,
    selectedRootTasksCount,
  };
}
